import React, { useState } from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { fmtRel, fmtBytes } from "@/lib/format";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
    AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { HardDrive, RefreshCcw, AlertTriangle, ShieldAlert, Trash2, CheckCircle2, MinusCircle, ChevronDown, ChevronRight, Eraser } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

const SEV = {
    critical: { cls: "text-red-400 border-red-500/40 bg-red-500/10", label: "Critical", Icon: ShieldAlert },
    error: { cls: "text-orange-400 border-orange-500/40 bg-orange-500/10", label: "Error", Icon: AlertTriangle },
    warning: { cls: "text-amber-300 border-amber-500/40 bg-amber-500/10", label: "Warning", Icon: AlertTriangle },
    info: { cls: "text-sky-300 border-sky-500/40 bg-sky-500/10", label: "Info", Icon: Trash2 },
    ok: { cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", label: "Consistent", Icon: CheckCircle2 },
    skipped: { cls: "text-zinc-400 border-zinc-600/40 bg-zinc-700/10", label: "Skipped", Icon: MinusCircle },
};

const Row = ({ k, v, testid, accent }) => (
    <div className="flex items-center justify-between py-1 border-b border-zinc-800/60 last:border-0">
        <span className="text-xs text-zinc-500">{k}</span>
        <span data-testid={testid} className={`text-xs font-mono ${accent || "text-zinc-200"}`}>{v}</span>
    </div>
);

export const CloudStorageConsistencyPanel = ({ cloudId }) => {
    const { data, mutate } = useSWR(
        `/api/admin/clouds/${cloudId}/storage-consistency`, fetcher,
        { refreshInterval: 0 },
    );
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [open, setOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [repairBusy, setRepairBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [pruneUserData, setPruneUserData] = useState(false);
    const s = data || {};
    const sev = SEV[s.severity] || SEV.skipped;
    const checked = s.checked;
    const canRepair = checked && !s.skipped && s.severity !== "error"
        && ((s.orphaned_in_s3 || 0) > 0 || (s.missing_appdata || 0) > 0
            || (s.missing_user_data || 0) > 0);

    const runCheck = async () => {
        setBusy(true); setErr(null); setResult(null);
        try {
            await api.post(`/api/admin/clouds/${cloudId}/storage-consistency/check`);
            await mutate();
        } catch (e) { setErr(formatApiError(e)); }
        setBusy(false);
    };

    const startRepair = async () => {
        setRepairBusy(true); setErr(null); setResult(null); setPruneUserData(false);
        try {
            // Dry-run always asks for the user-data preview (count + sample) so
            // the operator can decide in the dialog whether to purge them.
            const r = await api.post(`/api/admin/clouds/${cloudId}/storage-consistency/repair`, { dry_run: true, prune_missing_userdata: true });
            if (!r.data?.ok) { setErr(r.data?.error || "Preview failed"); }
            else { setPreview(r.data); setDialogOpen(true); }
        } catch (e) { setErr(formatApiError(e)); }
        setRepairBusy(false);
    };

    const applyRepair = async () => {
        setRepairBusy(true); setErr(null);
        try {
            const r = await api.post(`/api/admin/clouds/${cloudId}/storage-consistency/repair`, {
                dry_run: false, grace_seconds: preview?.grace_seconds ?? 86400,
                prune_missing_userdata: pruneUserData,
            });
            setResult(r.data);
            setDialogOpen(false);
            await mutate();
        } catch (e) { setErr(formatApiError(e)); setDialogOpen(false); }
        setRepairBusy(false);
    };

    const graceH = Math.round((preview?.grace_seconds ?? 86400) / 3600);

    return (
        <div className="cm-card p-5" data-testid="cloud-storage-consistency-panel">
            <div className="cm-h3 mb-3 flex items-center justify-between">
                <span className="flex items-center gap-1.5"><HardDrive size={14} /> Storage consistency (S3 ↔ DB)</span>
                <button
                    type="button"
                    data-testid="storage-consistency-check-button"
                    disabled={busy || repairBusy}
                    onClick={runCheck}
                    className="text-[11px] font-mono text-zinc-500 hover:text-zinc-200 disabled:opacity-40 flex items-center gap-1"
                    title="Run the S3↔DB object diff on the worker VM now (otherwise automatically every 24h)"
                >
                    <RefreshCcw size={10} className={busy ? "animate-spin" : ""} /> {busy ? "checking…" : "Check now"}
                </button>
            </div>

            {!checked ? (
                <p className="text-xs text-zinc-500" data-testid="storage-consistency-never-checked">
                    Not checked yet. The check runs every 24&nbsp;h automatically — or click “Check now”.
                </p>
            ) : (
                <>
                    <div className="mb-3 flex items-center justify-between">
                        <span
                            data-testid="storage-consistency-severity"
                            className={`inline-flex items-center gap-1.5 text-xs font-mono px-2 py-0.5 rounded-full border ${sev.cls}`}
                        >
                            <sev.Icon size={12} /> {sev.label}
                        </span>
                        <span className="text-[11px] text-zinc-600">{s.last_run_at ? fmtRel(s.last_run_at) : "—"}</span>
                    </div>

                    {s.skipped ? (
                        <p className="text-xs text-zinc-500" data-testid="storage-consistency-skipped">
                            {s.reason || "No S3 object store — check not applicable."}
                        </p>
                    ) : (s.error && s.severity === "error") ? (
                        <div className="text-xs text-red-400/90 font-mono break-all" data-testid="storage-consistency-error">
                            {s.error}
                        </div>
                    ) : (
                        <>
                            <Row testid="sc-db-files" k="DB files (expected)" v={(s.db_files ?? 0).toLocaleString()} />
                            <Row testid="sc-s3-objects" k="S3 objects (present)" v={(s.s3_objects ?? 0).toLocaleString()} />
                            <Row
                                testid="sc-missing"
                                k="Missing in S3"
                                accent={s.missing_in_s3 > 0 ? "text-amber-300" : "text-emerald-400"}
                                v={s.missing_in_s3 ?? 0}
                            />
                            {s.missing_user_data > 0 && (
                                <Row
                                    testid="sc-missing-user"
                                    k="↳ of which user data"
                                    accent={s.missing_user_data_nonempty > 0 ? "text-red-400" : "text-amber-300"}
                                    v={`${s.missing_user_data} (${s.missing_user_data_nonempty || 0} with content)`}
                                />
                            )}
                            {s.missing_appdata > 0 && (
                                <Row testid="sc-missing-appdata" k="↳ of which appdata/cache" v={s.missing_appdata} accent="text-zinc-300" />
                            )}
                            <Row
                                testid="sc-orphaned"
                                k="Orphaned in S3 (cleanup)"
                                accent={s.orphaned_in_s3 > 0 ? "text-sky-300" : "text-zinc-200"}
                                v={s.orphaned_in_s3 ?? 0}
                            />

                            {canRepair && (
                                <button
                                    type="button"
                                    data-testid="storage-consistency-repair-button"
                                    disabled={repairBusy || busy}
                                    onClick={startRepair}
                                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 disabled:opacity-40 transition-colors"
                                    title="Delete orphaned S3 objects + remove appdata/cache leftovers (shows a preview first)"
                                >
                                    <Eraser size={12} className={repairBusy ? "animate-pulse" : ""} />
                                    {repairBusy ? "computing preview…" : "Clean up…"}
                                </button>
                            )}

                            {result && (
                                <div className="mt-3 text-xs font-mono rounded border border-emerald-500/30 bg-emerald-500/5 p-2 space-y-0.5" data-testid="storage-consistency-repair-result">
                                    <div className="text-emerald-300 flex items-center gap-1"><CheckCircle2 size={12} /> Cleaned</div>
                                    <div className="text-zinc-300">{(result.deleted_objects ?? 0).toLocaleString()} orphaned objects deleted (~{fmtBytes(result.deleted_bytes)})</div>
                                    <div className="text-zinc-300">{(result.appdata_pruned ?? 0).toLocaleString()} appdata/cache entries removed</div>
                                    {(result.user_data_pruned ?? 0) > 0 && <div className="text-red-300">{(result.user_data_pruned ?? 0).toLocaleString()} orphaned user-data entries removed from the DB</div>}
                                    {result.delete_errors > 0 && <div className="text-amber-300">{result.delete_errors} objects could not be deleted</div>}
                                </div>
                            )}

                            {((s.missing_sample || []).length > 0 || (s.orphaned_sample || []).length > 0) && (
                                <button
                                    type="button"
                                    data-testid="storage-consistency-toggle-samples"
                                    onClick={() => setOpen((o) => !o)}
                                    className="mt-2 text-[11px] font-mono text-zinc-500 hover:text-zinc-300 flex items-center gap-1"
                                >
                                    {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />} Show examples
                                </button>
                            )}
                            {open && (
                                <div className="mt-2 space-y-2">
                                    {(s.missing_sample || []).length > 0 && (
                                        <div data-testid="storage-consistency-missing-sample">
                                            <div className="text-[11px] uppercase tracking-wide text-zinc-600 mb-1">Missing objects</div>
                                            <div className="space-y-0.5 max-h-40 overflow-auto">
                                                {(s.missing_sample || []).map((m) => (
                                                    <div key={m.fileid} className="text-[11px] font-mono text-zinc-400 break-all">
                                                        <span className={m.storage?.startsWith("object::user:") ? "text-red-400" : "text-zinc-500"}>
                                                            [{m.storage?.startsWith("object::user:") ? "user" : "appdata"}]
                                                        </span>{" "}
                                                        {m.path} <span className="text-zinc-600">({m.size}B)</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {(s.orphaned_sample || []).length > 0 && (
                                        <div data-testid="storage-consistency-orphaned-sample">
                                            <div className="text-[11px] uppercase tracking-wide text-zinc-600 mb-1">Orphaned objects</div>
                                            <div className="text-[11px] font-mono text-zinc-500 break-all">
                                                {(s.orphaned_sample || []).join(", ")}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
            {err && <div className="mt-2 text-xs text-cm-danger" data-testid="storage-consistency-action-error">{err}</div>}

            <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <AlertDialogContent data-testid="storage-consistency-repair-dialog" className="bg-zinc-950 border-zinc-800">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-zinc-100">
                            <Eraser size={16} className="text-sky-300" /> Clean up storage?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="text-xs text-zinc-400 space-y-2 pt-1">
                                <div className="font-mono space-y-1">
                                    <div className="flex justify-between"><span>Delete orphaned S3 objects</span>
                                        <span className="text-sky-300" data-testid="repair-preview-orphans">{(preview?.orphaned_eligible ?? 0).toLocaleString()} (~{fmtBytes(preview?.orphaned_bytes_eligible)})</span></div>
                                    {(preview?.orphaned_skipped_recent ?? 0) > 0 && (
                                        <div className="flex justify-between text-zinc-500"><span>↳ protected (younger than {graceH}h)</span>
                                            <span data-testid="repair-preview-skipped">{(preview?.orphaned_skipped_recent ?? 0).toLocaleString()}</span></div>
                                    )}
                                    <div className="flex justify-between"><span>Remove appdata/cache leftovers</span>
                                        <span className="text-zinc-300" data-testid="repair-preview-appdata">{(preview?.appdata_dangling ?? 0).toLocaleString()}</span></div>
                                </div>
                                {(preview?.user_data_missing ?? 0) > 0 && (
                                    <div className="text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2 space-y-1.5" data-testid="repair-preview-userdata-warning">
                                        <div>⚠ <b>{preview.user_data_missing}</b> user-data entries point to missing S3 objects
                                            {(preview.user_data_missing_nonempty ?? 0) > 0
                                                ? ` (of which ${preview.user_data_missing_nonempty} with content — real, unrecoverable data loss)` : ""}.
                                            The files are irretrievably gone; the DB entries only make the affected files appear broken (500).</div>
                                        <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="repair-prune-userdata-label">
                                            <input
                                                type="checkbox"
                                                data-testid="repair-prune-userdata-checkbox"
                                                checked={pruneUserData}
                                                onChange={(e) => setPruneUserData(e.target.checked)}
                                                className="mt-0.5 accent-red-500"
                                            />
                                            <span className="text-red-200">Remove orphaned user-data entries from the DB now (incl. broken shares/tags). <b>Irreversible.</b></span>
                                        </label>
                                        {(preview?.user_data_sample || []).length > 0 && (
                                            <div className="max-h-28 overflow-auto text-[11px] font-mono text-red-200/80 pt-1 border-t border-red-500/20" data-testid="repair-preview-userdata-sample">
                                                {(preview.user_data_sample || []).map((m) => (
                                                    <div key={m.fileid} className="break-all">{m.path} <span className="text-red-300/60">({fmtBytes(m.size)})</span></div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="text-zinc-500">Orphaned objects are referenced by no file; appdata/cache is recreated by Nextcloud. This action is irreversible.</div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="storage-consistency-repair-cancel-button" disabled={repairBusy} className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="storage-consistency-repair-confirm-button"
                            disabled={repairBusy}
                            onClick={(e) => { e.preventDefault(); applyRepair(); }}
                            className="bg-sky-600 hover:bg-sky-500 text-white"
                        >
                            {repairBusy ? "deleting…" : "Delete now"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default CloudStorageConsistencyPanel;
